import { useEffect, useRef } from "react";

/**
 * Calls `onResume` when the user returns to the app (tab focus, page restore, or WebView foreground).
 * focus, pageshow and visibilitychange often fire together, so `onResume` must join an in-flight
 * refresh rather than restart it (e.g. `invalidateQueries(filters, { cancelRefetch: false })`).
 */
export function useAppResume(onResume: () => void, enabled = true) {
  const callback = useRef(onResume);
  useEffect(() => { callback.current = onResume; });
  useEffect(() => {
    if (!enabled) return;
    const resume = () => { if (document.visibilityState === "visible") callback.current(); };
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [enabled]);
}